import { useEffect, useState } from 'react';
import { useLazyGetCoCustomerAggregatedQuery } from 'src/services/coCustomerAnalyticsService';
import { useAppDispatch } from 'src/store';
import { setLastUpdate } from 'src/store/slices/analyticSlices';
import { ANALYTICS_COCUSTOMER_INITIAL_STATE as INITIAL_STATE } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCoCustomerInitialState';
import { AggregatedRequestParams } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCoCustomer.types';

const AGGREGATED_METRICS_INITIAL_STATE = {
  resolutionRate: INITIAL_STATE.resolutionRate,
  totalProcessedTickets: INITIAL_STATE.totalProcessedTickets,
  resolvedTickets: INITIAL_STATE.resolvedTickets,
  escalatedTickets: INITIAL_STATE.escalatedTickets,
  aiRepliesSent: INITIAL_STATE.aiRepliesSent,
  aiRepliesPerProcessedTicket: INITIAL_STATE.aiRepliesPerProcessedTicket,
  firstResponseTimeAvg: INITIAL_STATE.firstResponseTimeAvg,
  responseTimeAvg: INITIAL_STATE.responseTimeAvg,
  resolutionTimeAvg: INITIAL_STATE.resolutionTimeAvg,
};

/**
 * Fetches the aggregated co-customer analytics for the given request params and
 * exposes them as a normalized metrics object (falls back to INITIAL_STATE on 404).
 */
export const useCoCustomerAggregatedMetrics = (
  requestParams: AggregatedRequestParams,
  enabled: boolean,
) => {
  const dispatch = useAppDispatch();
  const [
    fetchAggregatedQuery,
    { data: aggregatedData, isSuccess, isFetching, isError, error, reset },
  ] = useLazyGetCoCustomerAggregatedQuery() as any;
  const [metrics, setMetrics] = useState(AGGREGATED_METRICS_INITIAL_STATE);

  useEffect(() => {
    if (enabled && requestParams) {
      fetchAggregatedQuery(requestParams);
    }
  }, [enabled, requestParams]);

  useEffect(() => {
    if (isError && error?.status === 404) {
      setMetrics(AGGREGATED_METRICS_INITIAL_STATE);
      reset();
    }
  }, [isError, error, reset]);

  useEffect(() => {
    if (aggregatedData) {
      const {
        resolutionRate,
        totalProcessedTickets,
        resolvedTickets,
        escalatedTickets,
        aiRepliesSent,
        aiRepliesPerProcessedTicket,
        firstResponseTimeAvg,
        responseTimeAvg,
        resolutionTimeAvg,
      } = aggregatedData;

      setMetrics({
        resolutionRate,
        totalProcessedTickets,
        resolvedTickets,
        escalatedTickets,
        aiRepliesSent,
        aiRepliesPerProcessedTicket,
        firstResponseTimeAvg,
        responseTimeAvg,
        resolutionTimeAvg,
      });
    }
  }, [aggregatedData]);

  useEffect(() => {
    if (isSuccess && !isFetching) {
      dispatch(setLastUpdate({ lastUpdate: new Date() }));
    }
  }, [isSuccess, isFetching, dispatch]);

  return { metrics, aggregatedData, isFetching };
};
